import { SCHEDULING_CATEGORY_COLORS, SCHEDULING_CATEGORY_COLOR_NAMES } from '@mg/shared';

/**
 * The colours a category's swatch picker offers: the palette minus the colours OTHER categories already
 * use, in palette order. The category being edited (`exceptId`) always keeps its own colour on offer,
 * even when another category uses it too, so renaming never changes the colour by itself. Once the
 * OTHER categories use all ten, the whole palette is offered again, so a colour can repeat (spec: "hide
 * colors already used by another category; if all are used, show the full palette"). The API never
 * refuses a repeat.
 */
export function availableColors(
  categories: ReadonlyArray<{ id: string; color: string }>,
  exceptId?: string,
): string[] {
  const taken = new Set(categories.filter((c) => c.id !== exceptId).map((c) => c.color.toUpperCase()));
  const own = categories.find((c) => c.id === exceptId)?.color.toUpperCase();
  // "All are used" is judged on the others' colours before the own one is put back. Put back first,
  // the list is never empty, and a category whose others use all ten could only keep its own colour.
  const free = SCHEDULING_CATEGORY_COLORS.filter((hex) => !taken.has(hex));
  if (free.length === 0) return [...SCHEDULING_CATEGORY_COLORS];
  return SCHEDULING_CATEGORY_COLORS.filter((hex) => hex === own || !taken.has(hex));
}

/** A palette colour's accessible name ("Orange"), for a swatch's label; the value itself if it is not in the palette. */
export function colorName(color: string): string {
  const at = SCHEDULING_CATEGORY_COLORS.findIndex((hex) => hex === color.toUpperCase());
  return at === -1 ? color : SCHEDULING_CATEGORY_COLOR_NAMES[at];
}
